import { giphyDarkCharcoal } from '@giphy/colors'
import { useEffect, useState } from 'react'
import { useKeyPress } from 'react-use'
import { CopilotUploadPreset } from 'shared/types/copilot'
import styled from 'styled-components'
import { Gradient } from 'types'
import { Button, PortalAlert, TextInput } from 'ui'
import { CopilotPresetAlert, CopilotPresetAlertInputSettings } from './types'
import {
    DEFAULT_INPUT_SETTINGS,
    continueGradientFromPresetAlert,
    continueLabelFromPresetAlert,
    inputSettingsFromPresetAlert,
} from './utils/preset-alert'

const DEFAULT_GRADIENT: Gradient = 'purple-indigo'
const DEFAULT_LABEL = 'Continue'

const Input = styled(TextInput)`
    margin-bottom: 1.8em;
    width: 100%;
`

type Props = {
    alert?: CopilotPresetAlert | null
    className?: string
    isOpen?: boolean
    preset?: CopilotUploadPreset
    onCancel: () => void
    onContinue: (inputValue: string) => void
}

const CopilotAlert = ({
    alert = null,
    className,
    isOpen = false,
    preset,
    onCancel: onCancelProp,
    onContinue: onContinueProp,
}: Props) => {
    const [gradient, setGradient] = useState<Gradient>(DEFAULT_GRADIENT)
    const [label, setLabel] = useState<string>(DEFAULT_LABEL)
    const [inputSettings, setInputSettings] = useState<CopilotPresetAlertInputSettings>(DEFAULT_INPUT_SETTINGS)
    const [inputValue, setInputValue] = useState<string>('')
    const isEscKeyPressed = useKeyPress('Escape')

    function onCancel() {
        onCancelProp()
        setInputValue('')
    }

    function onContinue() {
        onContinueProp(inputValue)
        setInputValue('')
    }

    useEffect(() => {
        if (isEscKeyPressed && isOpen) {
            onCancel()
        }
    }, [isEscKeyPressed])

    useEffect(() => {
        if (!alert) {
            setGradient(DEFAULT_GRADIENT)
            setLabel(DEFAULT_LABEL)
            return
        }

        const gradient = continueGradientFromPresetAlert(alert)
        const label = continueLabelFromPresetAlert(alert)
        const settings = inputSettingsFromPresetAlert(alert, preset)

        setGradient(gradient)
        setInputSettings(settings)
        setInputValue(settings.defaultValue || '')
        setLabel(label)
    }, [alert, preset])

    return (
        <PortalAlert blurred className={className} isOpen={isOpen}>
            {inputSettings.visible && (
                <Input
                    autoFocus
                    color={giphyDarkCharcoal}
                    placeholder={inputSettings.placeholder}
                    size="medium"
                    value={inputValue}
                    onChange={setInputValue}
                    onEnterKeyPress={() => onContinue()}
                />
            )}
            <Button size="medium" onClick={onCancel}>
                Cancel
            </Button>
            <Button
                disabled={inputSettings.required && !inputValue}
                gradient={gradient}
                size="medium"
                onClick={() => onContinue()}
            >
                {label}
            </Button>
        </PortalAlert>
    )
}

export default CopilotAlert
